import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ARTEN, WEEKDAYS } from "@/lib/training";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, CalendarPlus, CalendarClock } from "lucide-react";
import { toast } from "sonner";

function PillRow({ value, onChange, options, testIdPrefix, getKey = (o) => o, getLabel = (o) => o }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const k = getKey(opt);
        const active = value === k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onChange(k)}
            className={
              "min-h-[44px] px-4 rounded-md border text-sm font-medium transition flex-1 sm:flex-none " +
              (active
                ? "bg-brand border-brand text-white"
                : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
            }
            data-testid={`${testIdPrefix}-${k}`}
          >
            {getLabel(opt)}
          </button>
        );
      })}
    </div>
  );
}

export default function TrainingCreate() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("single");
  const [saving, setSaving] = useState(false);

  // Common
  const [art, setArt] = useState("mannschaftstraining");
  const [uhrzeit, setUhrzeit] = useState("17:30");
  const [endZeit, setEndZeit] = useState("19:00");
  const [ort, setOrt] = useState("SpA Am Dölzschgraben");

  // Single
  const [datum, setDatum] = useState("");

  // Series
  const [weekday, setWeekday] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const submit = async () => {
    if (!ort.trim()) {
      toast.error("Bitte einen Ort angeben");
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(uhrzeit)) {
      toast.error("Bitte eine gültige Startzeit eingeben");
      return;
    }
    if (endZeit && !/^\d{2}:\d{2}$/.test(endZeit)) {
      toast.error("Bitte eine gültige Endzeit eingeben");
      return;
    }
    if (endZeit && endZeit <= uhrzeit) {
      toast.error("Endzeit muss nach der Startzeit liegen");
      return;
    }
    setSaving(true);
    try {
      if (mode === "single") {
        if (!datum) { toast.error("Bitte ein Datum wählen"); setSaving(false); return; }
        const { data } = await api.post("/trainings", { datum, uhrzeit, end_zeit: endZeit || null, ort: ort.trim(), art });
        toast.success("Training angelegt");
        navigate(`/training/${data.id}`);
      } else {
        if (!startDate || !endDate) { toast.error("Start- und Enddatum nötig"); setSaving(false); return; }
        const { data } = await api.post("/trainings/series", {
          weekday, uhrzeit, end_zeit: endZeit || null, ort: ort.trim(), art, start_date: startDate, end_date: endDate,
        });
        toast.success(`${data.length} Termine angelegt`);
        navigate("/training");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <button
        onClick={() => navigate("/training")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="tc-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Neues Training</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">Termin anlegen</h1>
      </div>

      {/* Mode toggle */}
      <div className="grid grid-cols-2 gap-2 mb-6">
        <button
          onClick={() => setMode("single")}
          className={
            "flex items-center gap-2 p-4 rounded-md border transition text-left " +
            (mode === "single"
              ? "border-brand bg-brand/5 text-zinc-100"
              : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700")
          }
          data-testid="mode-single"
        >
          <CalendarPlus className={"h-5 w-5 " + (mode === "single" ? "text-brand-lighter" : "text-zinc-500")} />
          <div>
            <div className="font-medium text-zinc-100">Einzeltermin</div>
            <div className="text-xs text-zinc-500">Ein einzelnes Training</div>
          </div>
        </button>
        <button
          onClick={() => setMode("series")}
          className={
            "flex items-center gap-2 p-4 rounded-md border transition text-left " +
            (mode === "series"
              ? "border-brand bg-brand/5 text-zinc-100"
              : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700")
          }
          data-testid="mode-series"
        >
          <CalendarClock className={"h-5 w-5 " + (mode === "series" ? "text-brand-lighter" : "text-zinc-500")} />
          <div>
            <div className="font-medium text-zinc-100">Serientermin</div>
            <div className="text-xs text-zinc-500">Wöchentlich automatisch</div>
          </div>
        </button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6 space-y-5">
        <Field label="Trainingsart">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setArt("mannschaftstraining")}
              className={
                "min-h-[44px] px-4 rounded-md border text-sm font-medium transition flex-1 sm:flex-none " +
                (art === "mannschaftstraining"
                  ? "bg-brand border-brand text-white"
                  : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
              }
              data-testid="art-mannschaftstraining"
            >
              Mannschaftstraining
            </button>
            <button
              type="button"
              onClick={() => setArt("bonustraining")}
              className={
                "min-h-[44px] px-4 rounded-md border text-sm font-medium transition flex-1 sm:flex-none " +
                (art === "bonustraining"
                  ? "bg-brand border-brand text-white"
                  : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
              }
              data-testid="art-bonustraining"
            >
              Bonustraining
            </button>
            <button
              type="button"
              onClick={() => navigate("/leistungstest/neu")}
              className="min-h-[44px] px-4 rounded-md border text-sm font-medium transition flex-1 sm:flex-none bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100"
              data-testid="art-leistungstest"
            >
              Leistungstest
            </button>
          </div>
        </Field>

        {mode === "single" ? (
          <Field label="Datum">
            <Input
              type="date"
              value={datum}
              onChange={(e) => setDatum(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
              data-testid="tc-datum"
            />
          </Field>
        ) : (
          <>
            <Field label="Wochentag">
              <PillRow
                value={weekday}
                onChange={setWeekday}
                options={WEEKDAYS}
                getKey={(w) => w.idx}
                getLabel={(w) => w.short}
                testIdPrefix="wd"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Startdatum">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
                  data-testid="tc-start-date"
                />
              </Field>
              <Field label="Enddatum">
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
                  data-testid="tc-end-date"
                />
              </Field>
            </div>
          </>
        )}

        <Field label="Uhrzeit (Start – Ende)">
          <div className="flex items-center gap-2">
            <Input
              type="time"
              value={uhrzeit}
              onChange={(e) => setUhrzeit(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md max-w-[140px]"
              data-testid="tc-uhrzeit"
            />
            <span className="text-zinc-500">–</span>
            <Input
              type="time"
              value={endZeit}
              onChange={(e) => setEndZeit(e.target.value)}
              className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md max-w-[140px]"
              data-testid="tc-end-zeit"
            />
          </div>
        </Field>

        <Field label="Ort">
          <Input
            value={ort}
            onChange={(e) => setOrt(e.target.value)}
            placeholder="z. B. Sportplatz Süd"
            className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
            data-testid="tc-ort"
          />
        </Field>

        <Button
          onClick={submit}
          disabled={saving}
          className="w-full bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
          data-testid="tc-save-btn"
        >
          {saving ? "Speichern…" : mode === "single" ? "Training anlegen" : "Serie erstellen"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-zinc-300">{label}</Label>
      {children}
    </div>
  );
}
